/**
 * WorkspaceV2 Loader
 * Handles loading flat file format (workspace/v2) workspaces
 */

import { browserFileService } from '@/services/platform/browser';
import { odcsService } from '@/services/sdk/odcsService';
import { odpsService } from '@/services/sdk/odpsService';
import { cadsService } from '@/services/sdk/cadsService';
import { dbmvService } from '@/services/sdk/dbmvService';
import { bpmnService } from '@/services/sdk/bpmnService';
import { dmnService } from '@/services/sdk/dmnService';
import { knowledgeService } from '@/services/sdk/knowledgeService';
import { decisionService } from '@/services/sdk/decisionService';
import { sketchService } from '@/services/sdk/sketchService';
import * as yaml from 'js-yaml';
import { FileMigration } from '@/utils/fileMigration';
import {
  useValidationStore,
  parseValidationError,
  type ResourceType,
} from '@/stores/validationStore';
import type {
  Workspace,
  WorkspaceV2,
  DomainV2,
  SystemV2,
  RelationshipV2,
  CategorizedFiles,
  DomainFiles,
} from '@/types/workspace';
import type { Domain } from '@/types/domain';
import type { System } from '@/types/system';
import type { Table } from '@/types/table';
import type { Relationship } from '@/types/relationship';
import type { DataProduct } from '@/types/odps';
import type { ComputeAsset } from '@/types/cads';
import type { MetricView } from '@/types/metricView';
import type { BPMNProcess } from '@/types/bpmn';
import type { DMNDecision } from '@/types/dmn';
import type { KnowledgeArticle } from '@/types/knowledge';
import type { Decision } from '@/types/decision';
import type { Sketch } from '@/types/sketch';

/**
 * Result of loading a domain with all its resources
 */
interface DomainLoadResult {
  domain: Domain;
  tables: Table[];
  products: DataProduct[];
  assets: ComputeAsset[];
  metricViews: MetricView[];
  processes: BPMNProcess[];
  decisions: DMNDecision[];
  systems: System[];
  knowledgeArticles: KnowledgeArticle[];
  decisionRecords: Decision[];
  sketches: Sketch[];
  failedOdcsFiles: string[];
}

export class WorkspaceV2Loader {
  /**
   * Load workspace from flat file format (v2)
   * Matches SDK workspace-schema.json format
   * Supports both flat files and subdirectory organization
   */
  static async loadFromFiles(files: FileList): Promise<Workspace> {
    const fileArray = Array.from(files);

    // 1. Find and parse workspace.yaml (could be at root or have path prefix from directory picker)
    const workspaceFile = fileArray.find((f) => f.name.endsWith('.workspace.yaml'));

    if (!workspaceFile) {
      throw new Error('No workspace.yaml found in v2 format');
    }

    const workspaceContent = await browserFileService.readFile(workspaceFile);
    const workspaceV2 = yaml.load(workspaceContent) as WorkspaceV2;

    // SDK schema uses flat structure with 'name' at root level
    const workspaceName = workspaceV2.name;
    const workspaceId = workspaceV2.id;
    console.log('[WorkspaceV2Loader] Loaded workspace.yaml:', workspaceName);

    // 1.5. Try to load README.md and extract description
    const readmeFile = fileArray.find(
      (f) => f.name === 'README.md' || f.name.endsWith('/README.md')
    );
    if (readmeFile) {
      try {
        const readmeContent = await browserFileService.readFile(readmeFile);
        const extractedDescription = this.extractDescriptionFromReadme(readmeContent);
        if (extractedDescription && !workspaceV2.description) {
          workspaceV2.description = extractedDescription;
          console.log('[WorkspaceV2Loader] Extracted description from README.md');
        }
      } catch (error) {
        console.warn('[WorkspaceV2Loader] Failed to read README.md:', error);
      }
    }

    // 2. Categorize remaining files by pattern (handles both flat and subdirectory structures)
    // For subdirectory structure, webkitRelativePath contains the path
    const fileNames = fileArray.map((f) => (f as any).webkitRelativePath || f.name);

    // Debug: log all file names to see what's being loaded
    console.log('[WorkspaceV2Loader] All files being categorized:', fileNames);

    const categorized = this.categorizeFilesWithPaths(fileNames);

    console.log('[WorkspaceV2Loader] Categorized files:', {
      odcs: categorized.odcs.length,
      odps: categorized.odps.length,
      cads: categorized.cads.length,
      dbmv: categorized.dbmv.length,
      bpmn: categorized.bpmn.length,
      dmn: categorized.dmn.length,
      kb: categorized.kb.length,
      adr: categorized.adr.length,
      sketch: categorized.sketch.length,
    });

    // 3. Load each domain's resources - collect all resources
    const domainsSpec = workspaceV2.domains || [];
    const domainResults: DomainLoadResult[] = await Promise.all(
      domainsSpec.map(async (domainSpec) => {
        return await this.loadDomain(
          domainSpec,
          workspaceName,
          workspaceId,
          fileArray,
          categorized
        );
      })
    );

    // 4. Aggregate all resources from all domains
    const domains: Domain[] = [];
    const allTables: Table[] = [];
    const allProducts: DataProduct[] = [];
    const allAssets: ComputeAsset[] = [];
    const allMetricViews: MetricView[] = [];
    const allProcesses: BPMNProcess[] = [];
    const allDecisions: DMNDecision[] = [];
    const allSystems: System[] = [];
    const allKnowledgeArticles: KnowledgeArticle[] = [];
    const allDecisionRecords: Decision[] = [];
    const allSketches: Sketch[] = [];
    const allFailedOdcsFiles: string[] = [];

    for (const result of domainResults) {
      domains.push(result.domain);
      allTables.push(...result.tables);
      allProducts.push(...result.products);
      allAssets.push(...result.assets);
      allMetricViews.push(...result.metricViews);
      allProcesses.push(...result.processes);
      allDecisions.push(...result.decisions);
      allSystems.push(...result.systems);
      allKnowledgeArticles.push(...result.knowledgeArticles);
      allDecisionRecords.push(...result.decisionRecords);
      allSketches.push(...result.sketches);
      allFailedOdcsFiles.push(...result.failedOdcsFiles);
    }

    // 4.5 Load ALL KB articles and ADRs at workspace level
    // Domain is determined by domain_id in the file content, not filename
    const allKBFiles = fileArray.filter((f) => {
      const fileName = ((f as any).webkitRelativePath || f.name).split('/').pop() || '';
      return fileName.endsWith('.kb.yaml');
    });
    const allADRFiles = fileArray.filter((f) => {
      const fileName = ((f as any).webkitRelativePath || f.name).split('/').pop() || '';
      return fileName.endsWith('.adr.yaml');
    });

    if (allKBFiles.length > 0) {
      console.log(`[WorkspaceV2Loader] Loading ${allKBFiles.length} KB article(s)`);
      const kbArticles = await this.loadKnowledgeArticles(allKBFiles, {
        id: 'workspace',
        name: 'workspace',
      } as DomainV2);
      allKnowledgeArticles.push(...kbArticles);
    }

    if (allADRFiles.length > 0) {
      console.log(`[WorkspaceV2Loader] Loading ${allADRFiles.length} ADR(s)`);
      const adrRecords = await this.loadDecisionRecords(allADRFiles, {
        id: 'workspace',
        name: 'workspace',
      } as DomainV2);
      allDecisionRecords.push(...adrRecords);
    }

    // 4.6 Load ALL sketch files at workspace level
    // Domain is determined by domain_id in the file content, not filename
    const allSketchFiles = fileArray.filter((f) => {
      const fileName = ((f as any).webkitRelativePath || f.name).split('/').pop() || '';
      return fileName.endsWith('.sketch.json');
    });

    if (allSketchFiles.length > 0) {
      console.log(`[WorkspaceV2Loader] Loading ${allSketchFiles.length} sketch(es)`);
      const sketches = await this.loadSketches(allSketchFiles, {
        id: 'workspace',
        name: 'workspace',
      } as DomainV2);
      allSketches.push(...sketches);
    }

    // 5. Load relationships from workspace.yaml (SDK schema stores them at workspace level)
    const allRelationships: Relationship[] = this.loadRelationships(
      workspaceV2.relationships || [],
      workspaceId
    );

    // 5.5 Set domain_id on relationships based on source table's domain
    // Create a map of table ID to domain ID for quick lookup
    const tableIdToDomainId = new Map<string, string>();
    for (const table of allTables) {
      if (table.id && table.primary_domain_id) {
        tableIdToDomainId.set(table.id, table.primary_domain_id);
      }
    }

    // Update each relationship's domain_id based on its source table
    for (const rel of allRelationships) {
      const sourceTableId = rel.source_table_id || rel.source_id;
      if (sourceTableId) {
        const domainId = tableIdToDomainId.get(sourceTableId);
        if (domainId) {
          rel.domain_id = domainId;
        } else {
          // Fallback: try target table
          const targetTableId = rel.target_table_id || rel.target_id;
          if (targetTableId) {
            const targetDomainId = tableIdToDomainId.get(targetTableId);
            if (targetDomainId) {
              rel.domain_id = targetDomainId;
            }
          }
        }
      }
    }

    // Debug: Log table ID mapping issues
    if (allRelationships.length > 0 && allRelationships.filter((r) => r.domain_id).length === 0) {
      const sampleRel = allRelationships[0];
      const sampleTableIds = Array.from(tableIdToDomainId.keys()).slice(0, 5);
      if (sampleRel) {
        console.warn('[WorkspaceV2Loader] No relationships matched tables! Debug info:', {
          sampleRelSourceId: sampleRel.source_table_id || sampleRel.source_id,
          sampleRelTargetId: sampleRel.target_table_id || sampleRel.target_id,
          sampleLoadedTableIds: sampleTableIds,
          totalLoadedTables: tableIdToDomainId.size,
          totalRelationships: allRelationships.length,
        });
      }
    }

    console.log(
      `[WorkspaceV2Loader] Set domain_id on ${allRelationships.filter((r) => r.domain_id).length}/${allRelationships.length} relationship(s)`
    );

    // 6. Build workspace object with all resources attached (like V1 loader does)
    const workspace: Workspace & {
      tables?: Table[];
      relationships?: Relationship[];
      systems?: System[];
      products?: DataProduct[];
      assets?: ComputeAsset[];
      metricViews?: MetricView[];
      bpmnProcesses?: BPMNProcess[];
      dmnDecisions?: DMNDecision[];
      knowledgeArticles?: KnowledgeArticle[];
      decisionRecords?: Decision[];
      sketches?: Sketch[];
      failedOdcsFiles?: string[];
    } = {
      id: workspaceId,
      name: workspaceName,
      description: workspaceV2.description, // From workspace.yaml or extracted from README.md
      owner_id: workspaceV2.owner_id || 'offline-user',
      created_at: workspaceV2.created_at || new Date().toISOString(),
      last_modified_at: workspaceV2.last_modified_at || new Date().toISOString(),
      domains,
    };

    // Attach all loaded resources to workspace
    if (allTables.length > 0) {
      workspace.tables = allTables;
      console.log(`[WorkspaceV2Loader] Added ${allTables.length} table(s) to workspace`);
    }
    if (allRelationships.length > 0) {
      workspace.relationships = allRelationships;
      console.log(
        `[WorkspaceV2Loader] Added ${allRelationships.length} relationship(s) to workspace`
      );
    }
    if (allSystems.length > 0) {
      workspace.systems = allSystems;
      console.log(`[WorkspaceV2Loader] Added ${allSystems.length} system(s) to workspace`);
    }
    if (allProducts.length > 0) {
      workspace.products = allProducts;
      console.log(`[WorkspaceV2Loader] Added ${allProducts.length} product(s) to workspace`);
    }
    if (allAssets.length > 0) {
      workspace.assets = allAssets;
      console.log(`[WorkspaceV2Loader] Added ${allAssets.length} asset(s) to workspace`);
    }
    if (allMetricViews.length > 0) {
      workspace.metricViews = allMetricViews;
      console.log(`[WorkspaceV2Loader] Added ${allMetricViews.length} metric view(s) to workspace`);
    }
    if (allProcesses.length > 0) {
      workspace.bpmnProcesses = allProcesses;
      console.log(`[WorkspaceV2Loader] Added ${allProcesses.length} BPMN process(es) to workspace`);
    }
    if (allDecisions.length > 0) {
      workspace.dmnDecisions = allDecisions;
      console.log(`[WorkspaceV2Loader] Added ${allDecisions.length} DMN decision(s) to workspace`);
    }
    if (allKnowledgeArticles.length > 0) {
      workspace.knowledgeArticles = allKnowledgeArticles;
      console.log(
        `[WorkspaceV2Loader] Added ${allKnowledgeArticles.length} knowledge article(s) to workspace`
      );
    }
    if (allDecisionRecords.length > 0) {
      workspace.decisionRecords = allDecisionRecords;
      console.log(
        `[WorkspaceV2Loader] Added ${allDecisionRecords.length} decision record(s) to workspace`
      );
    }
    if (allSketches.length > 0) {
      workspace.sketches = allSketches;
      console.log(`[WorkspaceV2Loader] Added ${allSketches.length} sketch(es) to workspace`);
    }

    // Track failed ODCS files so the saver can protect them from deletion
    if (allFailedOdcsFiles.length > 0) {
      workspace.failedOdcsFiles = allFailedOdcsFiles;
      console.warn(
        `[WorkspaceV2Loader] ${allFailedOdcsFiles.length} ODCS file(s) failed to parse and will be preserved on save:`,
        allFailedOdcsFiles
      );
    }

    console.log('[WorkspaceV2Loader] Loaded workspace with', domains.length, 'domains');
    console.log('[WorkspaceV2Loader] Workspace summary:', {
      domains: domains.length,
      tables: allTables.length,
      relationships: allRelationships.length,
      systems: allSystems.length,
      products: allProducts.length,
      assets: allAssets.length,
      metricViews: allMetricViews.length,
      bpmnProcesses: allProcesses.length,
      dmnDecisions: allDecisions.length,
      knowledgeArticles: allKnowledgeArticles.length,
      decisionRecords: allDecisionRecords.length,
      sketches: allSketches.length,
    });

    return workspace;
  }

  /**
   * Load workspace from pre-loaded file contents (for bundled examples)
   * Accepts an array of {name, content} objects instead of File objects
   */
  static async loadFromStringFiles(
    files: Array<{ name: string; content: string }>
  ): Promise<Workspace> {
    // 1. Find and parse workspace.yaml
    const workspaceFile = files.find((f) => f.name.endsWith('.workspace.yaml'));

    if (!workspaceFile) {
      throw new Error('No workspace.yaml found in v2 format');
    }

    const workspaceV2 = yaml.load(workspaceFile.content) as WorkspaceV2;

    const workspaceName = workspaceV2.name;
    const workspaceId = workspaceV2.id;
    console.log('[WorkspaceV2Loader] Loaded workspace from strings:', workspaceName);

    // 2. Categorize files by pattern
    const fileNames = files.map((f) => f.name);
    const categorized = FileMigration.categorizeFiles(fileNames);

    console.log('[WorkspaceV2Loader] Categorized string files:', {
      odcs: categorized.odcs.length,
      odps: categorized.odps.length,
      cads: categorized.cads.length,
      dbmv: categorized.dbmv.length,
      bpmn: categorized.bpmn.length,
      dmn: categorized.dmn.length,
      kb: categorized.kb.length,
      adr: categorized.adr.length,
      sketch: categorized.sketch.length,
    });

    // 3. Load each domain's resources
    const domainsSpec = workspaceV2.domains || [];
    const domainResults: DomainLoadResult[] = await Promise.all(
      domainsSpec.map(async (domainSpec) => {
        return await this.loadDomainFromStrings(
          domainSpec,
          workspaceName,
          workspaceId,
          files,
          categorized
        );
      })
    );

    // 4. Aggregate all resources from all domains
    const domains: Domain[] = [];
    const allTables: Table[] = [];
    const allProducts: DataProduct[] = [];
    const allAssets: ComputeAsset[] = [];
    const allMetricViews: MetricView[] = [];
    const allProcesses: BPMNProcess[] = [];
    const allDecisions: DMNDecision[] = [];
    const allSystems: System[] = [];
    const allKnowledgeArticles: KnowledgeArticle[] = [];
    const allDecisionRecords: Decision[] = [];
    const allSketches: Sketch[] = [];
    const allFailedOdcsFiles: string[] = [];

    for (const result of domainResults) {
      domains.push(result.domain);
      allTables.push(...result.tables);
      allProducts.push(...result.products);
      allAssets.push(...result.assets);
      allMetricViews.push(...result.metricViews);
      allProcesses.push(...result.processes);
      allDecisions.push(...result.decisions);
      allSystems.push(...result.systems);
      allKnowledgeArticles.push(...result.knowledgeArticles);
      allDecisionRecords.push(...result.decisionRecords);
      allSketches.push(...result.sketches);
      allFailedOdcsFiles.push(...result.failedOdcsFiles);
    }

    // 4.5 Load ALL KB articles and ADRs at workspace level
    // Domain is determined by domain_id in the file content, not filename
    const allKBFiles = files.filter((f) => f.name.endsWith('.kb.yaml'));
    const allADRFiles = files.filter((f) => f.name.endsWith('.adr.yaml'));
    const allSketchFiles = files.filter((f) => f.name.endsWith('.sketch.json'));

    if (allKBFiles.length > 0) {
      console.log(`[WorkspaceV2Loader] Loading ${allKBFiles.length} KB article(s) from strings`);
      const kbArticles = await this.loadKnowledgeArticlesFromStrings(allKBFiles, {
        id: 'workspace',
        name: 'workspace',
      } as DomainV2);
      allKnowledgeArticles.push(...kbArticles);
    }

    if (allADRFiles.length > 0) {
      console.log(`[WorkspaceV2Loader] Loading ${allADRFiles.length} ADR(s) from strings`);
      const adrRecords = await this.loadDecisionRecordsFromStrings(allADRFiles, {
        id: 'workspace',
        name: 'workspace',
      } as DomainV2);
      allDecisionRecords.push(...adrRecords);
    }

    if (allSketchFiles.length > 0) {
      console.log(`[WorkspaceV2Loader] Loading ${allSketchFiles.length} sketch(es) from strings`);
      const sketches = await this.loadSketchesFromStrings(allSketchFiles, {
        id: 'workspace',
        name: 'workspace',
      } as DomainV2);
      allSketches.push(...sketches);
    }

    // 5. Load relationships
    const allRelationships: Relationship[] = this.loadRelationships(
      workspaceV2.relationships || [],
      workspaceId
    );

    // Set domain_id on relationships
    const tableIdToDomainId = new Map<string, string>();
    for (const table of allTables) {
      if (table.id && table.primary_domain_id) {
        tableIdToDomainId.set(table.id, table.primary_domain_id);
      }
    }

    for (const rel of allRelationships) {
      const sourceTableId = rel.source_table_id || rel.source_id;
      if (sourceTableId) {
        const domainId = tableIdToDomainId.get(sourceTableId);
        if (domainId) {
          rel.domain_id = domainId;
        }
      }
    }

    // 5.5. Try to load README.md and extract description (for string files)
    const readmeFile = files.find((f) => f.name === 'README.md');
    if (readmeFile && !workspaceV2.description) {
      const extractedDescription = this.extractDescriptionFromReadme(readmeFile.content);
      if (extractedDescription) {
        workspaceV2.description = extractedDescription;
        console.log('[WorkspaceV2Loader] Extracted description from README.md (strings)');
      }
    }

    // 6. Build workspace object
    const workspace: Workspace & {
      tables?: Table[];
      relationships?: Relationship[];
      systems?: System[];
      products?: DataProduct[];
      assets?: ComputeAsset[];
      metricViews?: MetricView[];
      bpmnProcesses?: BPMNProcess[];
      dmnDecisions?: DMNDecision[];
      knowledgeArticles?: KnowledgeArticle[];
      decisionRecords?: Decision[];
      sketches?: Sketch[];
      failedOdcsFiles?: string[];
    } = {
      id: workspaceId,
      name: workspaceName,
      description: workspaceV2.description, // From workspace.yaml or extracted from README.md
      owner_id: workspaceV2.owner_id || 'example-user',
      created_at: workspaceV2.created_at || new Date().toISOString(),
      last_modified_at: workspaceV2.last_modified_at || new Date().toISOString(),
      domains,
    };

    // Attach all loaded resources
    if (allTables.length > 0) workspace.tables = allTables;
    if (allRelationships.length > 0) workspace.relationships = allRelationships;
    if (allSystems.length > 0) workspace.systems = allSystems;
    if (allProducts.length > 0) workspace.products = allProducts;
    if (allAssets.length > 0) workspace.assets = allAssets;
    if (allMetricViews.length > 0) workspace.metricViews = allMetricViews;
    if (allProcesses.length > 0) workspace.bpmnProcesses = allProcesses;
    if (allDecisions.length > 0) workspace.dmnDecisions = allDecisions;
    if (allKnowledgeArticles.length > 0) workspace.knowledgeArticles = allKnowledgeArticles;
    if (allDecisionRecords.length > 0) workspace.decisionRecords = allDecisionRecords;
    if (allSketches.length > 0) workspace.sketches = allSketches;

    // Track failed ODCS files so the saver can protect them from deletion
    if (allFailedOdcsFiles.length > 0) {
      workspace.failedOdcsFiles = allFailedOdcsFiles;
      console.warn(
        `[WorkspaceV2Loader] ${allFailedOdcsFiles.length} ODCS file(s) failed to parse and will be preserved on save:`,
        allFailedOdcsFiles
      );
    }

    console.log('[WorkspaceV2Loader] Loaded workspace from strings:', {
      domains: domains.length,
      tables: allTables.length,
      relationships: allRelationships.length,
      systems: allSystems.length,
    });

    return workspace;
  }

  /**
   * Load a single domain's resources from flat files
   */
  private static async loadDomain(
    domainSpec: DomainV2,
    workspaceName: string,
    workspaceId: string,
    fileArray: File[],
    categorized: CategorizedFiles
  ): Promise<DomainLoadResult> {
    // Filter files for this domain
    const domainFiles = this.filterFilesByDomain(
      fileArray,
      categorized,
      workspaceName,
      domainSpec.name
    );

    console.log(`[WorkspaceV2Loader] Loading domain "${domainSpec.name}" with:`, {
      odcs: domainFiles.odcs.length,
      odps: domainFiles.odps.length,
      cads: domainFiles.cads.length,
      dbmv: domainFiles.dbmv.length,
      bpmn: domainFiles.bpmn.length,
      dmn: domainFiles.dmn.length,
      kb: domainFiles.kb.length,
      adr: domainFiles.adr.length,
      sketch: domainFiles.sketch.length,
    });

    // Load tables
    const { tables, failedFiles: failedOdcsFiles } = await this.loadTables(
      domainFiles.odcs,
      domainSpec,
      workspaceId
    );

    // Load products
    const products = await this.loadProducts(domainFiles.odps, domainSpec);

    // Load assets
    const assets = await this.loadAssets(domainFiles.cads, domainSpec);

    // Load metric views
    const { views: metricViews, viewSystemNameMap } = await this.loadMetricViews(
      domainFiles.dbmv,
      domainSpec,
      workspaceName,
      domainSpec.name
    );

    // Load processes
    const processes = await this.loadProcesses(domainFiles.bpmn, domainSpec);

    // Load DMN decisions
    const decisions = await this.loadDecisions(domainFiles.dmn, domainSpec);

    // KB articles, ADRs, and sketches are loaded at the workspace level (not per-domain)
    // Domain is determined by domain_id in the file content
    const knowledgeArticles: KnowledgeArticle[] = [];
    const decisionRecords: Decision[] = [];
    const sketches: Sketch[] = [];

    // Convert systems from DomainV2 format to System format
    // Pass failedOdcsFiles so systems with failed ODCS files preserve their table_ids from workspace.yaml
    const systems = this.loadSystems(
      domainSpec.systems || [],
      domainSpec.id,
      workspaceId,
      tables,
      failedOdcsFiles
    );

    // Auto-assign metric views to systems based on DBMV filename
    // DBMV files are named {workspace}_{domain}_{system}.dbmv.yaml
    this.assignMetricViewsToSystems(systems, metricViews, viewSystemNameMap);

    // Construct Domain object (SDK schema has simpler DomainV2 structure)
    const domain: Domain = {
      id: domainSpec.id,
      workspace_id: workspaceId,
      name: domainSpec.name,
      description: domainSpec.description,
      // SDK 2.3.0+: Load domain owner if present
      owner: domainSpec.owner,
      created_at: new Date().toISOString(),
      last_modified_at: new Date().toISOString(),
      systems: systems.map((s) => s.id),
      tables: tables.map((t) => t.id),
      products: products.map((p) => p.id),
      assets: assets.map((a) => a.id),
      metric_views: metricViews.map((mv) => mv.id),
      processes: processes.map((p) => p.id),
      decisions: decisions.map((d) => d.id),
      // Load view-specific positions for canvas nodes (tables, systems, assets)
      view_positions: domainSpec.view_positions,
      // SDK 2.3.0+: Load shared resources if present
      shared_resources: domainSpec.shared_resources,
    };

    return {
      domain,
      tables,
      products,
      assets,
      metricViews,
      processes,
      decisions,
      systems,
      knowledgeArticles,
      decisionRecords,
      sketches,
      failedOdcsFiles,
    };
  }

  /**
   * Load a single domain's resources from string files (for bundled examples)
   */
  private static async loadDomainFromStrings(
    domainSpec: DomainV2,
    workspaceName: string,
    workspaceId: string,
    files: Array<{ name: string; content: string }>,
    categorized: CategorizedFiles
  ): Promise<DomainLoadResult> {
    // Filter files for this domain
    const domainFiles = this.filterStringFilesByDomain(
      files,
      categorized,
      workspaceName,
      domainSpec.name
    );

    console.log(`[WorkspaceV2Loader] Loading domain "${domainSpec.name}" from strings with:`, {
      odcs: domainFiles.odcs.length,
      odps: domainFiles.odps.length,
      cads: domainFiles.cads.length,
      dbmv: domainFiles.dbmv.length,
      bpmn: domainFiles.bpmn.length,
      dmn: domainFiles.dmn.length,
      kb: domainFiles.kb.length,
      adr: domainFiles.adr.length,
      sketch: domainFiles.sketch.length,
    });

    // Load tables
    const { tables, failedFiles: failedOdcsFiles } = await this.loadTablesFromStrings(
      domainFiles.odcs,
      domainSpec,
      workspaceId
    );

    // Load products
    const products = await this.loadProductsFromStrings(domainFiles.odps, domainSpec);

    // Load assets
    const assets = await this.loadAssetsFromStrings(domainFiles.cads, domainSpec);

    // Load metric views
    const { views: metricViews, viewSystemNameMap } = await this.loadMetricViewsFromStrings(
      domainFiles.dbmv,
      domainSpec,
      workspaceName,
      domainSpec.name
    );

    // Load processes
    const processes = await this.loadProcessesFromStrings(domainFiles.bpmn, domainSpec);

    // Load DMN decisions
    const decisions = await this.loadDecisionsFromStrings(domainFiles.dmn, domainSpec);

    // KB articles, ADRs, and sketches are loaded at the workspace level (not per-domain)
    // Domain is determined by domain_id in the file content
    const knowledgeArticles: KnowledgeArticle[] = [];
    const decisionRecords: Decision[] = [];
    const sketches: Sketch[] = [];

    // Convert systems from DomainV2 format to System format
    // Pass failedOdcsFiles so systems with failed ODCS files preserve their table_ids
    const systems = this.loadSystems(
      domainSpec.systems || [],
      domainSpec.id,
      workspaceId,
      tables,
      failedOdcsFiles
    );

    // Auto-assign metric views to systems based on DBMV filename
    this.assignMetricViewsToSystems(systems, metricViews, viewSystemNameMap);

    // Construct Domain object
    const domain: Domain = {
      id: domainSpec.id,
      workspace_id: workspaceId,
      name: domainSpec.name,
      description: domainSpec.description,
      // SDK 2.3.0+: Load domain owner if present
      owner: domainSpec.owner,
      created_at: new Date().toISOString(),
      last_modified_at: new Date().toISOString(),
      systems: systems.map((s) => s.id),
      tables: tables.map((t) => t.id),
      products: products.map((p) => p.id),
      assets: assets.map((a) => a.id),
      metric_views: metricViews.map((mv) => mv.id),
      processes: processes.map((p) => p.id),
      decisions: decisions.map((d) => d.id),
      // Load view-specific positions for canvas nodes (tables, systems, assets)
      view_positions: domainSpec.view_positions,
      // SDK 2.3.0+: Load shared resources if present
      shared_resources: domainSpec.shared_resources,
    };

    return {
      domain,
      tables,
      products,
      assets,
      metricViews,
      processes,
      decisions,
      systems,
      knowledgeArticles,
      decisionRecords,
      sketches,
      failedOdcsFiles,
    };
  }

  /**
   * Filter string files by domain using naming convention
   */
  private static filterStringFilesByDomain(
    files: Array<{ name: string; content: string }>,
    categorized: CategorizedFiles,
    workspaceName: string,
    domainName: string
  ): {
    odcs: Array<{ name: string; content: string }>;
    odps: Array<{ name: string; content: string }>;
    cads: Array<{ name: string; content: string }>;
    dbmv: Array<{ name: string; content: string }>;
    bpmn: Array<{ name: string; content: string }>;
    dmn: Array<{ name: string; content: string }>;
    kb: Array<{ name: string; content: string }>;
    adr: Array<{ name: string; content: string }>;
    sketch: Array<{ name: string; content: string }>;
  } {
    // Match files that start with {workspace}_{domain}_ OR are exactly {workspace}_{domain}.{ext}
    const prefixWithUnderscore = `${workspaceName}_${domainName}_`.toLowerCase();
    const exactDomainPrefix = `${workspaceName}_${domainName}.`.toLowerCase();

    // Helper to get just the filename from a path (handles odcs/filename.odcs.yaml)
    const getFileName = (path: string): string => {
      const parts = path.split('/');
      return parts[parts.length - 1] || path;
    };

    const filterByPrefix = (fileNames: string[]): Array<{ name: string; content: string }> => {
      return files.filter((f) => {
        // Check if file is in the categorized list (compare by filename only, not full path)
        const fileName = getFileName(f.name);
        const isInCategory = fileNames.some((p) => getFileName(p) === fileName);
        // Extract just the filename (without directory path) for prefix matching
        const fileNameLower = fileName.toLowerCase();
        // Match either:
        // 1. {workspace}_{domain}_{something}.ext (e.g., opendatamodelling_global_risk_table.odcs.yaml)
        // 2. {workspace}_{domain}.ext (e.g., opendatamodelling_global_risk.odcs.yaml)
        const matchesPrefix =
          fileNameLower.startsWith(prefixWithUnderscore) ||
          fileNameLower.startsWith(exactDomainPrefix);
        return isInCategory && matchesPrefix;
      });
    };

    return {
      odcs: filterByPrefix(categorized.odcs),
      odps: filterByPrefix(categorized.odps),
      cads: filterByPrefix(categorized.cads),
      dbmv: filterByPrefix(categorized.dbmv),
      bpmn: filterByPrefix(categorized.bpmn),
      dmn: filterByPrefix(categorized.dmn),
      kb: filterByPrefix(categorized.kb),
      adr: filterByPrefix(categorized.adr),
      sketch: filterByPrefix(categorized.sketch),
    };
  }

  /**
   * Load ODCS tables from string content
   */
  private static async loadTablesFromStrings(
    files: Array<{ name: string; content: string }>,
    domainSpec: DomainV2,
    workspaceId: string
  ): Promise<{ tables: Table[]; failedFiles: string[] }> {
    const tables: Table[] = [];
    const failedFiles: string[] = [];

    for (const file of files) {
      try {
        const parsed = await odcsService.parseYAML(file.content);

        // Extract ALL tables from multi-table contracts
        if (parsed && typeof parsed === 'object' && 'tables' in parsed) {
          const workspace = parsed as any;
          if (workspace.tables && Array.isArray(workspace.tables) && workspace.tables.length > 0) {
            for (const table of workspace.tables) {
              table.primary_domain_id = domainSpec.id;
              table.workspace_id = workspaceId;
              table.visible_domains = [domainSpec.id];
              console.log(
                `[WorkspaceV2Loader] Loaded table "${table.name}" with ID: ${table.id} from ${file.name}`
              );
              tables.push(table);
            }
            console.log(
              `[WorkspaceV2Loader] Loaded ${workspace.tables.length} table(s) from ${file.name}`
            );
          }
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load table from ${file.name}:`, error);
        // Record validation issue for the failed file
        this.recordValidationIssue(error, 'table', file.name, file.name);
        // Track the failed file name so it can be preserved on save
        const fileName = file.name.includes('/')
          ? file.name.split('/').pop() || file.name
          : file.name;
        failedFiles.push(fileName);
        console.warn(
          `[WorkspaceV2Loader] ODCS file "${fileName}" failed to parse - will be preserved on next save`
        );
      }
    }

    return { tables, failedFiles };
  }

  /**
   * Helper to record validation issues to the validation store
   */
  private static recordValidationIssue(
    error: unknown,
    resourceType: ResourceType,
    resourceId: string,
    resourceName: string,
    filePath?: string
  ): void {
    const issues = parseValidationError(error, resourceType, resourceId, resourceName, filePath);
    if (issues.length > 0) {
      useValidationStore
        .getState()
        .addIssues(
          issues.map(({ id: _id, createdAt: _createdAt, isActive: _isActive, ...rest }) => rest)
        );
      console.log(
        `[WorkspaceV2Loader] Recorded ${issues.length} validation issue(s) for ${resourceType}: ${resourceName}`
      );
    }
  }

  /**
   * Load ODPS products from string content
   */
  private static async loadProductsFromStrings(
    files: Array<{ name: string; content: string }>,
    domainSpec: DomainV2
  ): Promise<DataProduct[]> {
    const products: DataProduct[] = [];

    for (const file of files) {
      try {
        const product = await odpsService.parseYAML(file.content);

        if (product && typeof product === 'object' && 'id' in product) {
          (product as any).domain_id = domainSpec.id;
          products.push(product as DataProduct);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load product from ${file.name}:`, error);
      }
    }

    return products;
  }

  /**
   * Load CADS assets from string content
   */
  private static async loadAssetsFromStrings(
    files: Array<{ name: string; content: string }>,
    domainSpec: DomainV2
  ): Promise<ComputeAsset[]> {
    const assets: ComputeAsset[] = [];

    for (const file of files) {
      try {
        const asset = await cadsService.parseYAML(file.content);

        if (asset && typeof asset === 'object' && 'id' in asset) {
          (asset as any).domain_id = domainSpec.id;
          assets.push(asset as ComputeAsset);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load asset from ${file.name}:`, error);
      }
    }

    return assets;
  }

  /**
   * Load DBMV metric views from string content
   */
  private static async loadMetricViewsFromStrings(
    files: Array<{ name: string; content: string }>,
    domainSpec: DomainV2,
    workspaceName: string,
    domainName: string
  ): Promise<{ views: MetricView[]; viewSystemNameMap: Map<string, string> }> {
    const views: MetricView[] = [];
    const viewSystemNameMap = new Map<string, string>();

    // Build prefix to extract system name from filename
    // Pattern: {workspace}_{domain}_{system}.dbmv.yaml
    const prefix = `${workspaceName}_${domainName}_`.toLowerCase();

    for (const file of files) {
      try {
        const parsed = await dbmvService.parseYAML(file.content);

        // Extract system name from filename
        const fileName = file.name.split('/').pop() || file.name;
        let systemName: string | undefined;
        const fileNameLower = fileName.toLowerCase();
        if (fileNameLower.startsWith(prefix)) {
          systemName = fileName.substring(prefix.length).replace(/\.dbmv\.yaml$/i, '');
        }

        for (const view of parsed) {
          view.domain_id = domainSpec.id;
          views.push(view);
          if (systemName) {
            viewSystemNameMap.set(view.id, systemName);
          }
        }

        if (parsed.length > 0) {
          console.log(
            `[WorkspaceV2Loader] Loaded ${parsed.length} metric view(s) from ${file.name}${systemName ? ` (system: ${systemName})` : ''}`
          );
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load metric view from ${file.name}:`, error);
      }
    }

    return { views, viewSystemNameMap };
  }

  /**
   * Load BPMN processes from string content
   */
  private static async loadProcessesFromStrings(
    files: Array<{ name: string; content: string }>,
    domainSpec: DomainV2
  ): Promise<BPMNProcess[]> {
    const processes: BPMNProcess[] = [];

    for (const file of files) {
      try {
        const process = await bpmnService.parseXML(file.content);

        if (process && typeof process === 'object' && 'id' in process) {
          (process as any).domain_id = domainSpec.id;
          processes.push(process as BPMNProcess);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load process from ${file.name}:`, error);
      }
    }

    return processes;
  }

  /**
   * Load DMN decisions from string content
   */
  private static async loadDecisionsFromStrings(
    files: Array<{ name: string; content: string }>,
    domainSpec: DomainV2
  ): Promise<DMNDecision[]> {
    const decisions: DMNDecision[] = [];

    for (const file of files) {
      try {
        const decision = await dmnService.parseXML(file.content);

        if (decision && typeof decision === 'object' && 'id' in decision) {
          (decision as any).domain_id = domainSpec.id;
          decisions.push(decision as DMNDecision);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load decision from ${file.name}:`, error);
      }
    }

    return decisions;
  }

  /**
   * Load knowledge articles from string content
   */
  private static async loadKnowledgeArticlesFromStrings(
    files: Array<{ name: string; content: string }>,
    _domainSpec: DomainV2
  ): Promise<KnowledgeArticle[]> {
    const articles: KnowledgeArticle[] = [];

    for (const file of files) {
      try {
        // Pass file path for better validation error reporting
        const article = await knowledgeService.parseKnowledgeYaml(file.content, file.name);

        if (article && typeof article === 'object' && 'id' in article) {
          // Preserve domain_id from the file content as-is
          articles.push(article as KnowledgeArticle);
        }
      } catch (error) {
        console.error(
          `[WorkspaceV2Loader] Failed to load knowledge article from ${file.name}:`,
          error
        );
        this.recordValidationIssue(error, 'knowledge_article', file.name, file.name, file.name);
      }
    }

    return articles;
  }

  /**
   * Load decision records from string content
   */
  private static async loadDecisionRecordsFromStrings(
    files: Array<{ name: string; content: string }>,
    _domainSpec: DomainV2
  ): Promise<Decision[]> {
    const records: Decision[] = [];

    for (const file of files) {
      try {
        // Pass file path for better validation error reporting
        const record = await decisionService.parseDecisionYaml(file.content, file.name);

        if (record && typeof record === 'object' && 'id' in record) {
          // Preserve domain_id from the file content as-is
          records.push(record as Decision);
        }
      } catch (error) {
        console.error(
          `[WorkspaceV2Loader] Failed to load decision record from ${file.name}:`,
          error
        );
        this.recordValidationIssue(error, 'decision_record', file.name, file.name, file.name);
      }
    }

    return records;
  }

  /**
   * Load sketches from string content
   */
  private static async loadSketchesFromStrings(
    files: Array<{ name: string; content: string }>,
    _domainSpec: DomainV2
  ): Promise<Sketch[]> {
    const sketches: Sketch[] = [];

    for (const file of files) {
      try {
        const sketch = await sketchService.parseJSON(file.content);

        if (sketch && typeof sketch === 'object' && 'id' in sketch) {
          // Preserve domain_id from the file content as-is
          sketches.push(sketch as Sketch);
          const isGlobal = !(sketch as any).domain_id;
          console.log(
            `[WorkspaceV2Loader] Loaded sketch: ${sketch.name}${isGlobal ? ' (global)' : ` (domain: ${(sketch as any).domain_id})`}`
          );
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load sketch from ${file.name}:`, error);
      }
    }

    return sketches;
  }

  /**
   * Load systems from workspace.yaml domain spec
   */
  private static loadSystems(
    systemSpecs: SystemV2[],
    domainId: string,
    _workspaceId: string,
    tables: Table[],
    failedOdcsFiles: string[] = []
  ): System[] {
    const systems: System[] = [];

    for (const spec of systemSpecs) {
      let tableIds: string[] = [];

      // PRIORITY 1: Use table_ids from workspace.yaml if they match loaded tables
      if (spec.table_ids && spec.table_ids.length > 0) {
        // Check how many of these IDs actually match loaded tables
        const matchedIds = spec.table_ids.filter((id) => tables.some((t) => t.id === id));

        if (matchedIds.length > 0) {
          tableIds = matchedIds;
          console.log(
            `[WorkspaceV2Loader] System "${spec.name}" matched ${matchedIds.length}/${spec.table_ids.length} table_ids from workspace.yaml`
          );
        } else if (failedOdcsFiles.length > 0) {
          // table_ids exist but none match, AND there were ODCS parse failures
          // Preserve the original table_ids from workspace.yaml to prevent data loss
          tableIds = [...spec.table_ids];
          console.warn(
            `[WorkspaceV2Loader] System "${spec.name}" has ${spec.table_ids.length} table_ids preserved from workspace.yaml (ODCS file failed to parse - preserving to prevent data loss)`
          );
        } else {
          // table_ids exist but none match - fall through to metadata.system_id matching
          console.log(
            `[WorkspaceV2Loader] System "${spec.name}" has ${spec.table_ids.length} table_ids in workspace.yaml but none match loaded tables - using metadata.system_id fallback`
          );
        }
      }

      // PRIORITY 2: If no tables matched via table_ids, find tables by metadata.system_id
      // This handles the case where tables were regenerated with new IDs but have system_id in metadata
      if (tableIds.length === 0) {
        const systemTables = tables.filter((t) => {
          // Check if table has system_id in metadata matching this system's ID
          const tableSystemId = (t as any).metadata?.system_id;
          return tableSystemId === spec.id;
        });

        if (systemTables.length > 0) {
          tableIds = systemTables.map((t) => t.id);
          console.log(
            `[WorkspaceV2Loader] System "${spec.name}" matched ${tableIds.length} table(s) via metadata.system_id`
          );
        }
      }

      // PRIORITY 3: Last resort - match by naming convention (legacy support)
      if (tableIds.length === 0) {
        const systemTables = tables.filter((t) => {
          const tableName = t.name?.toLowerCase() || '';
          const systemName = spec.name?.toLowerCase() || '';
          return tableName.includes(systemName) || systemName.includes(tableName);
        });

        if (systemTables.length > 0) {
          tableIds = systemTables.map((t) => t.id);
          console.log(
            `[WorkspaceV2Loader] System "${spec.name}" matched ${tableIds.length} table(s) via naming convention fallback`
          );
        }
      }

      // Get asset_ids and metric_view_ids from workspace.yaml if present
      const assetIds = spec.asset_ids || [];
      const metricViewIds = spec.metric_view_ids || [];

      const system: System = {
        id: spec.id,
        name: spec.name,
        description: spec.description,
        domain_id: domainId,
        // SDK 2.3.0+: Use system_type from workspace.yaml, fallback to 'database'
        system_type: spec.system_type || 'database',
        // SDK 2.3.0+: Load connection_string if present
        connection_string: spec.connection_string,
        table_ids: tableIds,
        asset_ids: assetIds,
        metric_view_ids: metricViewIds,
        created_at: new Date().toISOString(),
        last_modified_at: new Date().toISOString(),
      };

      // Update tables with system linkage (set primary_system_id on matched tables)
      for (const tableId of tableIds) {
        const table = tables.find((t) => t.id === tableId);
        if (table) {
          (table as any).primary_system_id = spec.id;
        }
      }

      systems.push(system);
      console.log(
        `[WorkspaceV2Loader] Loaded system "${spec.name}" with ${tableIds.length} table(s), ${assetIds.length} asset(s), ${metricViewIds.length} metric view(s)`
      );
    }

    return systems;
  }

  /**
   * Auto-assign metric views to systems based on DBMV filename system name extraction.
   * Only assigns if the system doesn't already have metric_view_ids from workspace.yaml.
   */
  private static assignMetricViewsToSystems(
    systems: System[],
    metricViews: MetricView[],
    viewSystemNameMap: Map<string, string>
  ): void {
    if (metricViews.length === 0 || viewSystemNameMap.size === 0) return;

    for (const system of systems) {
      // Skip if system already has metric_view_ids from workspace.yaml
      if (system.metric_view_ids && system.metric_view_ids.length > 0) continue;

      // Find metric views whose DBMV filename matches this system name (case-insensitive)
      const systemNameLower = system.name.toLowerCase();
      const matchedViewIds = metricViews
        .filter((v) => {
          const viewSystemName = viewSystemNameMap.get(v.id);
          return viewSystemName && viewSystemName.toLowerCase() === systemNameLower;
        })
        .map((v) => v.id);

      if (matchedViewIds.length > 0) {
        system.metric_view_ids = matchedViewIds;
        console.log(
          `[WorkspaceV2Loader] Auto-assigned ${matchedViewIds.length} metric view(s) to system "${system.name}" from DBMV filename`
        );
      }
    }
  }

  /**
   * Load relationships from workspace.yaml
   */
  private static loadRelationships(
    relationshipSpecs: RelationshipV2[],
    workspaceId: string
  ): Relationship[] {
    return relationshipSpecs.map((spec) => {
      // Map cardinality to source/target cardinality
      let sourceCardinality: '0' | '1' | 'N' = '1';
      let targetCardinality: '0' | '1' | 'N' = '1';

      if (spec.cardinality === 'one_to_many') {
        targetCardinality = 'N';
      } else if (spec.cardinality === 'many_to_many') {
        sourceCardinality = 'N';
        targetCardinality = 'N';
      }

      // Map relationship_type to RelationshipType
      let relType: 'one-to-one' | 'one-to-many' | 'many-to-many' = 'one-to-many';
      if (spec.cardinality === 'one_to_one') {
        relType = 'one-to-one';
      } else if (spec.cardinality === 'many_to_many') {
        relType = 'many-to-many';
      }

      return {
        id: spec.id,
        workspace_id: workspaceId,
        domain_id: '', // Will be determined by the tables' domain
        source_id: spec.source_table_id,
        target_id: spec.target_table_id,
        source_type: 'table' as const,
        target_type: 'table' as const,
        source_table_id: spec.source_table_id,
        target_table_id: spec.target_table_id,
        type: relType,
        source_cardinality: sourceCardinality,
        target_cardinality: targetCardinality,
        description: spec.notes,
        notes: spec.notes,
        color: spec.color,
        owner: spec.owner,
        // Connection point handles for edge positioning on canvas
        source_handle: spec.source_handle,
        target_handle: spec.target_handle,
        // SDK 2.3.0+ relationship fields
        source_key: spec.source_key,
        target_key: spec.target_key,
        label: spec.label,
        flow_direction: spec.flow_direction,
        infrastructure_type: spec.infrastructure_type as any, // Map string to InfrastructureType
        relationship_type: spec.relationship_type as any, // Map to SDKRelationshipType
        foreign_key_details: spec.foreign_key_details,
        etl_job_metadata: spec.etl_job_metadata,
        visual_metadata: spec.visual_metadata,
        contact_details: spec.contact_details,
        sla: spec.sla,
        model_type: 'physical' as const,
        is_circular: false,
        created_at: new Date().toISOString(),
        last_modified_at: new Date().toISOString(),
      };
    });
  }

  /**
   * Filter files by domain using naming convention
   * Handles both flat files and subdirectory structure
   */
  private static filterFilesByDomain(
    fileArray: File[],
    categorized: CategorizedFiles,
    workspaceName: string,
    domainName: string
  ): DomainFiles {
    // Match files that start with {workspace}_{domain}_ OR are exactly {workspace}_{domain}.{ext}
    const prefixWithUnderscore = `${workspaceName}_${domainName}_`.toLowerCase();
    const exactDomainPrefix = `${workspaceName}_${domainName}.`.toLowerCase();

    // Helper to get filename from path or file.name
    const getFileName = (path: string) => {
      const parts = path.split('/');
      return parts[parts.length - 1];
    };

    const filterByPrefix = (filePaths: string[]): File[] => {
      const matched: File[] = [];

      for (const f of fileArray) {
        // Get the path (webkitRelativePath for directory picker, or name for file picker)
        const filePath = (f as any).webkitRelativePath || f.name;
        const fileName = getFileName(filePath);

        if (!fileName) continue;

        // Check if this file path is in our categorized list
        const isInCategory = filePaths.some((p) => getFileName(p) === fileName);

        // Match either:
        // 1. {workspace}_{domain}_{something}.ext (e.g., opendatamodelling_global_risk_table.odcs.yaml)
        // 2. {workspace}_{domain}.ext (e.g., opendatamodelling_global_risk.odcs.yaml)
        const fileNameLower = fileName.toLowerCase();
        const matchesPrefix =
          fileNameLower.startsWith(prefixWithUnderscore) ||
          fileNameLower.startsWith(exactDomainPrefix);

        if (isInCategory && matchesPrefix) {
          matched.push(f);
        }
      }
      return matched;
    };

    return {
      odcs: filterByPrefix(categorized.odcs),
      odps: filterByPrefix(categorized.odps),
      cads: filterByPrefix(categorized.cads),
      dbmv: filterByPrefix(categorized.dbmv),
      bpmn: filterByPrefix(categorized.bpmn),
      dmn: filterByPrefix(categorized.dmn),
      kb: filterByPrefix(categorized.kb),
      adr: filterByPrefix(categorized.adr),
      sketch: filterByPrefix(categorized.sketch),
    };
  }

  /**
   * Load ODCS tables from files
   */
  private static async loadTables(
    files: File[],
    domainSpec: DomainV2,
    workspaceId: string
  ): Promise<{ tables: Table[]; failedFiles: string[] }> {
    const tables: Table[] = [];
    const failedFiles: string[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const parsed = await odcsService.parseYAML(content);

        // SDK returns ODCSWorkspace with tables array, extract ALL tables (supports multi-table contracts)
        if (parsed && typeof parsed === 'object' && 'tables' in parsed) {
          const workspace = parsed as any;
          if (workspace.tables && Array.isArray(workspace.tables) && workspace.tables.length > 0) {
            for (const table of workspace.tables) {
              table.primary_domain_id = domainSpec.id;
              table.workspace_id = workspaceId;
              table.visible_domains = [domainSpec.id];

              console.log(
                `[WorkspaceV2Loader] Loaded table "${table.name}" with ID: ${table.id} from ${file.name}`
              );
              tables.push(table);
            }
            console.log(
              `[WorkspaceV2Loader] Loaded ${workspace.tables.length} table(s) from ${file.name}`
            );
          }
        }
      } catch (error) {
        const fileName =
          ((file as any).webkitRelativePath || file.name).split('/').pop() || file.name;
        failedFiles.push(fileName);
        console.error(`[WorkspaceV2Loader] Failed to load table from ${file.name}:`, error);
        console.warn(
          `[WorkspaceV2Loader] ODCS file "${fileName}" failed to parse - will be preserved on next save`
        );
        // Record validation issue so it appears in the ValidationWarnings panel
        this.recordValidationIssue(error, 'table', fileName, fileName, fileName);
      }
    }

    return { tables, failedFiles };
  }

  /**
   * Load ODPS products from files
   */
  private static async loadProducts(files: File[], domainSpec: DomainV2): Promise<DataProduct[]> {
    const products: DataProduct[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const product = await odpsService.parseYAML(content);

        if (product && typeof product === 'object' && 'id' in product) {
          (product as any).domain_id = domainSpec.id;
          products.push(product as DataProduct);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load product from ${file.name}:`, error);
      }
    }

    return products;
  }

  /**
   * Load CADS assets from files
   */
  private static async loadAssets(files: File[], domainSpec: DomainV2): Promise<ComputeAsset[]> {
    const assets: ComputeAsset[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const asset = await cadsService.parseYAML(content);

        if (asset && typeof asset === 'object' && 'id' in asset) {
          (asset as any).domain_id = domainSpec.id;
          assets.push(asset as ComputeAsset);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load asset from ${file.name}:`, error);
      }
    }

    return assets;
  }

  /**
   * Load DBMV metric views from files
   */
  private static async loadMetricViews(
    files: File[],
    domainSpec: DomainV2,
    workspaceName: string,
    domainName: string
  ): Promise<{ views: MetricView[]; viewSystemNameMap: Map<string, string> }> {
    const views: MetricView[] = [];
    const viewSystemNameMap = new Map<string, string>();

    // Build prefix to extract system name from filename
    // Pattern: {workspace}_{domain}_{system}.dbmv.yaml
    const prefix = `${workspaceName}_${domainName}_`.toLowerCase();

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const parsed = await dbmvService.parseYAML(content);

        // Extract system name from filename
        const fileName = file.name.split('/').pop() || file.name;
        let systemName: string | undefined;
        const fileNameLower = fileName.toLowerCase();
        if (fileNameLower.startsWith(prefix)) {
          systemName = fileName.substring(prefix.length).replace(/\.dbmv\.yaml$/i, '');
        }

        for (const view of parsed) {
          view.domain_id = domainSpec.id;
          views.push(view);
          if (systemName) {
            viewSystemNameMap.set(view.id, systemName);
          }
        }

        if (parsed.length > 0) {
          console.log(
            `[WorkspaceV2Loader] Loaded ${parsed.length} metric view(s) from ${file.name}${systemName ? ` (system: ${systemName})` : ''}`
          );
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load metric view from ${file.name}:`, error);
      }
    }

    return { views, viewSystemNameMap };
  }

  /**
   * Load BPMN processes from files
   */
  private static async loadProcesses(files: File[], domainSpec: DomainV2): Promise<BPMNProcess[]> {
    const processes: BPMNProcess[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const process = await bpmnService.parseXML(content);

        if (process && typeof process === 'object' && 'id' in process) {
          (process as any).domain_id = domainSpec.id;
          processes.push(process as BPMNProcess);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load process from ${file.name}:`, error);
      }
    }

    return processes;
  }

  /**
   * Load DMN decisions from files
   */
  private static async loadDecisions(files: File[], domainSpec: DomainV2): Promise<DMNDecision[]> {
    const decisions: DMNDecision[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const decision = await dmnService.parseXML(content);

        if (decision && typeof decision === 'object' && 'id' in decision) {
          (decision as any).domain_id = domainSpec.id;
          decisions.push(decision as DMNDecision);
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load decision from ${file.name}:`, error);
      }
    }

    return decisions;
  }

  /**
   * Load knowledge articles from .kb.yaml files
   */
  private static async loadKnowledgeArticles(
    files: File[],
    _domainSpec: DomainV2
  ): Promise<KnowledgeArticle[]> {
    const articles: KnowledgeArticle[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const article = await knowledgeService.parseKnowledgeYaml(content);

        if (article && typeof article === 'object' && 'id' in article) {
          // Preserve domain_id from the file content as-is
          // If domain_id is null/undefined → global article
          // If domain_id has a value → domain-specific article
          articles.push(article as KnowledgeArticle);
          const isGlobal = !(article as any).domain_id;
          console.log(
            `[WorkspaceV2Loader] Loaded knowledge article: ${article.title}${isGlobal ? ' (global)' : ` (domain: ${(article as any).domain_id})`}`
          );
        }
      } catch (error) {
        console.error(
          `[WorkspaceV2Loader] Failed to load knowledge article from ${file.name}:`,
          error
        );
      }
    }

    return articles;
  }

  /**
   * Load decision records (ADRs) from .adr.yaml files
   */
  private static async loadDecisionRecords(
    files: File[],
    _domainSpec: DomainV2
  ): Promise<Decision[]> {
    const decisions: Decision[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const decision = await decisionService.parseDecisionYaml(content);

        if (decision && typeof decision === 'object' && 'id' in decision) {
          // Preserve domain_id from the file content as-is
          decisions.push(decision as Decision);
          const isGlobal = !(decision as any).domain_id;
          console.log(
            `[WorkspaceV2Loader] Loaded decision record: ${decision.title}${isGlobal ? ' (global)' : ` (domain: ${(decision as any).domain_id})`}`
          );
        }
      } catch (error) {
        console.error(
          `[WorkspaceV2Loader] Failed to load decision record from ${file.name}:`,
          error
        );
      }
    }

    return decisions;
  }

  /**
   * Load sketches from .sketch.json files
   */
  private static async loadSketches(files: File[], _domainSpec: DomainV2): Promise<Sketch[]> {
    const sketches: Sketch[] = [];

    for (const file of files) {
      try {
        const content = await browserFileService.readFile(file);
        const sketch = await sketchService.parseJSON(content);

        if (sketch && typeof sketch === 'object' && 'id' in sketch) {
          // Preserve domain_id from the file content as-is
          sketches.push(sketch as Sketch);
          const isGlobal = !(sketch as any).domain_id;
          console.log(
            `[WorkspaceV2Loader] Loaded sketch: ${sketch.name}${isGlobal ? ' (global)' : ` (domain: ${(sketch as any).domain_id})`}`
          );
        }
      } catch (error) {
        console.error(`[WorkspaceV2Loader] Failed to load sketch from ${file.name}:`, error);
      }
    }

    return sketches;
  }

  /**
   * Categorize files by type, handling both flat and subdirectory structures
   * Supports paths like "odcs/file.odcs.yaml" or just "file.odcs.yaml"
   */
  private static categorizeFilesWithPaths(filePaths: string[]): CategorizedFiles {
    const workspacePattern = /\.workspace\.yaml$/;
    const odcsPattern = /\.odcs\.yaml$/;
    const odpsPattern = /\.odps\.yaml$/;
    const cadsPattern = /\.cads\.yaml$/;
    const dbmvPattern = /\.dbmv\.yaml$/;
    const bpmnPattern = /\.bpmn$/;
    const dmnPattern = /\.dmn$/;
    const kbPattern = /\.kb\.yaml$/;
    const adrPattern = /\.adr\.yaml$/;
    const sketchPattern = /\.sketch\.json$/;

    // Extract just the filename from paths for matching
    const getFileName = (path: string): string => {
      const parts = path.split('/');
      return parts[parts.length - 1] || path;
    };

    return {
      workspace: filePaths.find((f) => workspacePattern.test(getFileName(f))),
      odcs: filePaths.filter((f) => odcsPattern.test(getFileName(f))),
      odps: filePaths.filter((f) => odpsPattern.test(getFileName(f))),
      cads: filePaths.filter((f) => cadsPattern.test(getFileName(f))),
      dbmv: filePaths.filter((f) => dbmvPattern.test(getFileName(f))),
      bpmn: filePaths.filter((f) => bpmnPattern.test(getFileName(f))),
      dmn: filePaths.filter((f) => dmnPattern.test(getFileName(f))),
      kb: filePaths.filter((f) => kbPattern.test(getFileName(f))),
      adr: filePaths.filter((f) => adrPattern.test(getFileName(f))),
      sketch: filePaths.filter((f) => sketchPattern.test(getFileName(f))),
    };
  }

  /**
   * Extract description from README.md content
   * Looks for the first paragraph after the title (# Title) and before ## Overview
   */
  private static extractDescriptionFromReadme(content: string): string | null {
    const lines = content.split('\n');
    let inDescription = false;
    const descriptionLines: string[] = [];

    for (const line of lines) {
      // Skip the title line
      if (line.startsWith('# ')) {
        inDescription = true;
        continue;
      }

      // Stop at ## Overview or any other heading
      if (line.startsWith('## ')) {
        break;
      }

      // Collect non-empty lines as description
      if (inDescription) {
        const trimmed = line.trim();
        // Skip placeholder text
        if (trimmed && trimmed !== '_No description provided._') {
          descriptionLines.push(trimmed);
        }
      }
    }

    const description = descriptionLines.join('\n').trim();
    return description || null;
  }
}
